import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import KanbanBoard from "@/components/KanbanBoard";
import DealsTable from "@/components/DealsTable";
import ViewToggle from "@/components/ViewToggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["#1F2937", "#1F2937", "#1F2937", "#1F2937", "#1F2937", "#1F2937", "#1F2937", "#1F2937"];

const LOAN_STAGES = [
  "Prospect", "Draft", "Submitted", "Initial Review", "Assigned",
  "Underwriting", "Conditional Approval", "Need Additional Documents",
  "Approved", "Funded", "Declined",
];

function StageTile({ stage, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`overview-stage-${stage.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-left group hover:bg-slate-800/[0.03] transition-colors py-4 px-2 -mx-2"
      title={`Open Files filtered by ${stage}`}
    >
      <div className="small-caps text-slate-500 group-hover:text-[#1F2937]">{stage}</div>
      <div className="mt-3 font-semibold text-[40px] leading-none text-[#1F2937]">{count ?? 0}</div>
    </button>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [m, setM] = useState({});
  const [breakdownRole, setBreakdownRole] = useState("loan_rep");
  const [breakdown, setBreakdown] = useState({ people: [] });
  const [view, setView] = useState(() => localStorage.getItem("vn_pipeline_view") || "table");
  useEffect(() => localStorage.setItem("vn_pipeline_view", view), [view]);
  useEffect(() => { api.get("/metrics/admin").then((r) => setM(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/metrics/team-breakdown?role=${breakdownRole}`)
      .then((r) => setBreakdown(r.data))
      .catch(() => setBreakdown({ people: [] }));
  }, [breakdownRole]);
  const stageData = Object.entries(m.by_stage || {}).map(([name, value]) => ({ name, value }));
  const productData = (m.by_product || []).filter((p) => p.count > 0);

  return (
    <div className="max-w-[1360px] space-y-16" data-testid="admin-dashboard">
      <header>
        <div className="small-caps text-slate-500">Overview</div>
        <h1 className="mt-6 font-semibold text-[80px] leading-[0.9] tracking-tight text-[#1F2937]">
          The <span className="italic">firm.</span>
        </h1>
      </header>

      <section className="hair-y py-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-y-4 gap-x-8" data-testid="overview-stage-tiles">
        {LOAN_STAGES.map((s) => (
          <StageTile
            key={s}
            stage={s}
            count={(m.by_stage || {})[s] ?? 0}
            onClick={() => nav(`/app/applications?stage=${encodeURIComponent(s)}`)}
          />
        ))}
      </section>

      <div className="flex items-center justify-end">
        <ViewToggle value={view} onChange={setView} testid="admin-view-toggle" />
      </div>
      {view === "kanban" ? (
        <KanbanBoard title="Pipeline" subtitle="Drag files across positions" />
      ) : (
        <DealsTable title="Pipeline" subtitle="All deals" />
      )}

      <section className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-8">
          <div className="small-caps text-slate-500">Pipeline by Stage</div>
          <h2 className="mt-3 font-semibold text-[32px] text-[#1F2937]">Positions across the book</h2>
          <div className="mt-8 h-80 border-t border-slate-200 pt-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageData} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                <XAxis dataKey="name" fontSize={10} interval={0} angle={-25} textAnchor="end" height={60} stroke="#1F2937" tick={{ opacity: 0.6 }} />
                <YAxis fontSize={11} stroke="#1F2937" tick={{ opacity: 0.6 }} />
                <Tooltip cursor={{ fill: "rgba(11,19,31,0.04)" }} contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(11,19,31,0.15)", borderRadius: 0, fontFamily: "DM Sans" }} />
                <Bar dataKey="value" fill="#1F2937" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l lg:border-slate-200">
          <div className="small-caps text-slate-500">Allocation by Loan Program</div>
          <h2 className="mt-3 font-semibold text-[28px] text-[#1F2937]">Mix</h2>
          <div className="mt-8 h-80">
            {productData.length === 0 ? (
              <div className="h-full grid place-items-center font-semibold text-[16px] text-slate-400">No allocations yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={productData} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={95} paddingAngle={1} stroke="#FFFFFF">
                    {productData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(11,19,31,0.15)", borderRadius: 0, fontFamily: "DM Sans" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      <section data-testid="team-breakdown">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="small-caps text-slate-500">Applications by owner</div>
            <h2 className="mt-3 font-semibold text-[32px] text-[#1F2937]">
              Total files per {breakdownRole === "loan_rep" ? "loan officer" : "broker"}
            </h2>
          </div>
          <div className="w-56">
            <div className="small-caps text-slate-500 mb-1">Filter by role</div>
            <Select value={breakdownRole} onValueChange={setBreakdownRole}>
              <SelectTrigger
                data-testid="team-breakdown-role"
                className="border-0 shadow-none rounded-none px-0 bg-transparent font-semibold text-[17px] h-9 focus:ring-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="loan_rep">Loan Officers</SelectItem>
                <SelectItem value="broker">Brokers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-8">
          <div className="grid grid-cols-12 py-3 border-b border-slate-200 small-caps text-slate-500">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1 text-right">Pipeline</div>
            <div className="col-span-1 text-right">Funded</div>
            <div className="col-span-2 text-right">Funded volume</div>
          </div>
          {breakdown.people.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-12 items-baseline py-4 border-b border-slate-200"
              data-testid={`team-row-${p.id}`}
            >
              <div className="col-span-4">
                <div className="font-semibold text-[17px] text-[#1F2937]">{p.name || "—"}</div>
                <div className="small-caps text-slate-400 mt-0.5">{p.title || p.company || ""}</div>
              </div>
              <div className="col-span-3 text-[13px] text-slate-600 truncate">{p.email}</div>
              <div className="col-span-1 text-right font-mono text-[15px] text-[#1F2937]" data-testid={`team-total-${p.id}`}>{p.total}</div>
              <div className="col-span-1 text-right font-mono text-[13px] text-slate-600">{p.pipeline}</div>
              <div className="col-span-1 text-right font-mono text-[13px] text-slate-600">{p.funded}</div>
              <div className="col-span-2 text-right font-mono text-[13px] text-[#1F2937]">{fmtMoney(p.funded_volume)}</div>
            </div>
          ))}
          {breakdown.people.length === 0 && (
            <div className="py-16 text-center font-semibold text-[16px] text-slate-500">
              No {breakdownRole === "loan_rep" ? "loan officers" : "brokers"} yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
